import { initializeApp } from "firebase/app";
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Button from "senf-atomic-design-system/src/components/core/button/Button";

const firebaseConfig = {
  apiKey: "AIzaSyBbVacMvYfdUG96Ez8UVWbXZPO3f4HnjGY",
  authDomain: "senf-dev.firebaseapp.com",
  databaseURL:
    "https://senf-dev-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "senf-dev",
  storageBucket: "senf-dev.appspot.com",
  messagingSenderId: "665148664729",
  appId: "1:665148664729:web:0cde2c0ef4fe389235c108",
};

// Initialize Firebase app
const firebaseApp = initializeApp(firebaseConfig, "images-downloader");
const db = getFirestore(firebaseApp);

interface ImageData {
  url: string;
  name: string;
  folder: string;
  id: string;
}

const INITIAL_DISPLAY_COUNT = 100;
const LOAD_MORE_COUNT = 100;

const ImagesPage = () => {
  const [allImages, setAllImages] = useState<ImageData[]>([]);
  const [displayedImages, setDisplayedImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const requestsRef = collection(db, "utopia/general/requests");
      let q = query(
        requestsRef,
        orderBy("createdAt", "desc"),
        limit(LOAD_MORE_COUNT * 2),
      ); // Get more docs to ensure we have enough images

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      setStatus("Loading more images from Firestore...");
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        setStatus("No more images to load.");
        return;
      }

      const newImages: ImageData[] = [];

      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const requestId = docSnapshot.id;

        if (data.imageObjects && Array.isArray(data.imageObjects)) {
          data.imageObjects.forEach(
            (imgObj: { name?: string; url?: string }, index: number) => {
              if (imgObj.name?.startsWith("rendering") && imgObj.url) {
                newImages.push({
                  url: imgObj.url,
                  name: imgObj.name,
                  folder: requestId,
                  id: `${requestId}_${imgObj.name}_${index}`,
                });
              }
            },
          );
        }
      });

      // Limit to LOAD_MORE_COUNT
      const imagesToAdd = newImages.slice(0, LOAD_MORE_COUNT);

      if (imagesToAdd.length > 0) {
        // Save scroll position before update
        scrollPositionRef.current = containerRef.current?.scrollTop || 0;

        setAllImages((prev) => [...prev, ...imagesToAdd]);
        setDisplayedImages((prev) => [...prev, ...imagesToAdd]);
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        setStatus(
          `Loaded ${imagesToAdd.length} images. Total: ${allImages.length + imagesToAdd.length}.`,
        );
      }

      setHasMore(snapshot.docs.length >= LOAD_MORE_COUNT * 2);
    } catch (error) {
      console.error("Error loading more images:", error);
      setStatus(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  }, [lastDoc, allImages.length]);

  const loadInitialImages = useCallback(async () => {
    setLoading(true);
    setStatus("Loading images from Firestore...");

    try {
      const requestsRef = collection(db, "utopia/general/requests");
      const q = query(
        requestsRef,
        orderBy("createdAt", "desc"),
        limit(INITIAL_DISPLAY_COUNT * 3),
      ); // Get more docs to ensure we have enough images

      setStatus("Querying Firestore for requests...");
      const snapshot = await getDocs(q);
      console.log(`Found ${snapshot.docs.length} request documents`);

      const images: ImageData[] = [];

      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const requestId = docSnapshot.id;

        if (data.imageObjects && Array.isArray(data.imageObjects)) {
          data.imageObjects.forEach(
            (imgObj: { name?: string; url?: string }, index: number) => {
              if (imgObj.name?.startsWith("rendering") && imgObj.url) {
                images.push({
                  url: imgObj.url,
                  name: imgObj.name,
                  folder: requestId,
                  id: `${requestId}_${imgObj.name}_${index}`,
                });
              }
            },
          );
        }
      });

      // Limit to INITIAL_DISPLAY_COUNT
      const imagesToShow = images.slice(0, INITIAL_DISPLAY_COUNT);

      console.log(
        `Extracted ${imagesToShow.length} rendering images from ${snapshot.docs.length} documents`,
      );

      setAllImages(imagesToShow);
      setDisplayedImages(imagesToShow);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length >= INITIAL_DISPLAY_COUNT * 3);
      setStatus(`Loaded ${imagesToShow.length} images from Firestore.`);
    } catch (error) {
      console.error("Error loading images:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setStatus(`Error: ${errorMessage}. Check console for details.`);
      console.error("Full error details:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadImage = async (image: ImageData) => {
    setDownloading(image.id);
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();

      // Create download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${image.folder}_${image.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error(`Error downloading ${image.name}:`, error);
      setStatus(`Error downloading ${image.name}`);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    loadInitialImages();
  }, [loadInitialImages]);

  // Restore scroll position after images are loaded
  useLayoutEffect(() => {
    if (scrollPositionRef.current > 0 && containerRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = scrollPositionRef.current;
          scrollPositionRef.current = 0; // Reset after restoring
        }
      });
    }
  });

  return (
    <div
      ref={containerRef}
      style={{
        padding: "2rem",
        maxWidth: "1400px",
        margin: "0 auto",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ marginBottom: "0.5rem" }}>Images Gallery</h1>
        <p style={{ marginBottom: "0" }}>
          Images from Firebase Storage that start with "rendering..." from
          folders in utopia/requests/
        </p>
      </div>

      {status && (
        <div style={{ marginBottom: "1rem", color: "#666" }}>
          <p>{status}</p>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <p>Loading images...</p>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            {displayedImages.map((image) => (
              <div
                key={image.id}
                style={{
                  position: "relative",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "white",
                }}
              >
                <img
                  src={image.url}
                  alt={image.name}
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <div
                  style={{
                    padding: "0.5rem",
                    fontSize: "0.875rem",
                    background: "#f5f5f5",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {image.folder}
                  </div>
                  <div
                    style={{
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {image.name}
                  </div>
                  <Button
                    onClick={() => downloadImage(image)}
                    disabled={downloading === image.id}
                    text={
                      downloading === image.id ? "Downloading..." : "Download"
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
              <Button
                onClick={loadMore}
                disabled={loading}
                text={
                  loading
                    ? "Loading..."
                    : `Load More (${LOAD_MORE_COUNT} images)`
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImagesPage;
